import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
  Mail, 
  Send, 
  Clock, 
  CheckCircle,
  ArrowRight,
  Trash2,
  Eye,
  RefreshCw,
  Loader2,
  MessageSquare
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Emails = () => {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('drafts');

  useEffect(() => {
    fetchEmails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchEmails = async () => {
    try {
      const response = await axios.get(`${API}/emails`, { withCredentials: true });
      setEmails(response.data);
    } catch (error) {
      toast.error('Failed to load emails');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedEmail) return;
    try {
      await axios.put(`${API}/emails/${selectedEmail.email_id}`, {
        email_id: selectedEmail.email_id,
        approved: true,
        subject: selectedEmail.subject,
        body: selectedEmail.body
      }, { withCredentials: true });
      toast.success('Email approved');
      setDialogOpen(false);
      fetchEmails();
    } catch (error) {
      toast.error('Failed to approve email');
    }
  };

  const handleSend = async (emailId) => {
    try {
      await axios.post(`${API}/emails/${emailId}/send`, {}, { withCredentials: true });
      toast.success('Email sent');
      setDialogOpen(false);
      fetchEmails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    }
  };

  const handleDelete = async (emailId) => {
    if (!window.confirm('Delete this email draft?')) return;
    try {
      await axios.delete(`${API}/emails/${emailId}`, { withCredentials: true });
      toast.success('Email deleted');
      fetchEmails();
    } catch (error) {
      toast.error('Failed to delete email');
    }
  };

  const drafts = emails.filter(e => e.status === 'draft');
  const sent = emails.filter(e => e.status === 'sent');
  const repliedCount = sent.filter(e => e.replied).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-forest"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="emails-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-2xl sm:text-3xl text-foreground">Email Outreach</h1>
          <p className="text-muted-foreground mt-1">Review and send AI-drafted emails. Replies are detected automatically via webhook.</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
        <Card className="bg-white border-stone-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold">{drafts.filter(e => !e.approved).length}</p>
                <p className="text-xs text-muted-foreground">Pending Review</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-stone-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold">{drafts.filter(e => e.approved).length}</p>
                <p className="text-xs text-muted-foreground">Ready to Send</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-stone-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Send className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold">{sent.length}</p>
                <p className="text-xs text-muted-foreground">Sent</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-stone-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Mail className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold">{emails.length}</p>
                <p className="text-xs text-muted-foreground">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-stone-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <MessageSquare className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold">{repliedCount}</p>
                <p className="text-xs text-muted-foreground">Replied</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="drafts" data-testid="tab-drafts">
            Drafts ({drafts.length})
          </TabsTrigger>
          <TabsTrigger value="sent" data-testid="tab-sent">
            Sent ({sent.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="drafts" className="mt-4">
          {drafts.length > 0 ? (
            <div className="space-y-3">
              {drafts.map((email) => (
                <Card 
                  key={email.email_id} 
                  className="bg-white border-stone-100 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => {
                    setSelectedEmail(email);
                    setDialogOpen(true);
                  }}
                  data-testid={`email-draft-${email.email_id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{email.subject}</p>
                        <p className="text-sm text-muted-foreground truncate mt-1">{email.body.substring(0, 100)}...</p>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant={email.approved ? 'default' : 'secondary'} className={email.approved ? 'bg-blue-600' : ''}>
                            {email.approved ? 'Approved' : 'Pending Review'}
                          </Badge>
                          <Badge variant="outline">{email.email_type}</Badge>
                          <span className="text-xs text-muted-foreground">
                            {new Date(email.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {email.approved && (
                          <Button
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSend(email.email_id);
                            }}
                            className="bg-wheat hover:bg-wheat-dark text-white"
                          >
                            <Send className="h-4 w-4 mr-1" />
                            Send
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(email.email_id);
                          }}
                          className="text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="bg-white border-stone-100">
              <CardContent className="text-center py-12">
                <Mail className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
                <h3 className="font-heading font-semibold text-lg mb-2">No drafts</h3>
                <p className="text-muted-foreground mb-4">Go to a lead's detail page to draft an email</p>
                <Button asChild className="bg-forest hover:bg-forest-dark text-white">
                  <Link to="/pipeline">View Pipeline</Link>
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="sent" className="mt-4">
          {sent.length > 0 ? (
            <div className="space-y-3">
              {sent.map((email) => (
                <Card 
                  key={email.email_id} 
                  className="bg-white border-stone-100 cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => {
                    setSelectedEmail(email);
                    setDialogOpen(true);
                  }}
                  data-testid={`email-sent-${email.email_id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{email.subject}</p>
                        <p className="text-sm text-muted-foreground truncate mt-1">{email.body.substring(0, 100)}...</p>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge className="bg-green-600">Sent</Badge>
                          {email.opened && <Badge variant="outline" className="text-green-600 border-green-600">Opened</Badge>}
                          {email.replied && <Badge variant="outline" className="text-blue-600 border-blue-600">Replied</Badge>}
                          <span className="text-xs text-muted-foreground">
                            Sent {new Date(email.sent_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      <Eye className="h-5 w-5 text-muted-foreground" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="bg-white border-stone-100">
              <CardContent className="text-center py-12">
                <Send className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
                <h3 className="font-heading font-semibold text-lg mb-2">No sent emails</h3>
                <p className="text-muted-foreground">Approve and send drafts to see them here</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Email Detail Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Email Details</DialogTitle>
          </DialogHeader>
          {selectedEmail && (
            <div className="space-y-4 py-4">
              <div>
                <Label>Subject</Label>
                <Input
                  value={selectedEmail.subject}
                  onChange={(e) => setSelectedEmail({ ...selectedEmail, subject: e.target.value })}
                  readOnly={selectedEmail.status === 'sent'}
                />
              </div>
              <div>
                <Label>Body</Label>
                <Textarea
                  value={selectedEmail.body}
                  onChange={(e) => setSelectedEmail({ ...selectedEmail, body: e.target.value })}
                  rows={12}
                  readOnly={selectedEmail.status === 'sent'}
                />
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant={selectedEmail.status === 'sent' ? 'default' : 'secondary'} className={selectedEmail.status === 'sent' ? 'bg-green-600' : ''}>
                  {selectedEmail.status}
                </Badge>
                {selectedEmail.approved && <Badge variant="outline">Approved</Badge>}
                <Badge variant="outline">{selectedEmail.email_type}</Badge>
                <Link 
                  to={`/leads/${selectedEmail.lead_id}`}
                  className="text-sm text-forest hover:underline flex items-center gap-1"
                >
                  View Lead
                  <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Close</Button>
            {selectedEmail && selectedEmail.status !== 'sent' && !selectedEmail.approved && (
              <Button onClick={handleApprove} className="bg-forest hover:bg-forest-dark text-white">
                Approve Email
              </Button>
            )}
            {selectedEmail && selectedEmail.approved && selectedEmail.status !== 'sent' && (
              <Button 
                onClick={() => handleSend(selectedEmail.email_id)} 
                className="bg-wheat hover:bg-wheat-dark text-white"
              >
                <Send className="mr-2 h-4 w-4" />
                Send Email
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Emails;
